import React, { useState, useContext, useRef } from "react"
import { FirebaseContext } from "../Firebase"
import { WingsModal } from "@wingspans-ui"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faImages, faTrash as faTrashSolid } from "@fortawesome/free-solid-svg-icons"
import { faTrash as faTrashRegular } from "@fortawesome/pro-regular-svg-icons"
import imageCompression from "browser-image-compression"
import { FormattedMessage } from "react-intl"
import WallpaperImage from "../profile-image/WallpaperImage"
import { deleteField } from "firebase/firestore";
import "./portfolio-wallpaper-image-modal.css"
import "./portfolio-profile-image-modal.css"

let fileReader
if (typeof window !== "undefined") {
  fileReader = new FileReader()
}

const WallpaperImageModal = () => {
  const { firebase, profile } = useContext(FirebaseContext)
  const [error, setError] = useState(null)
  // null represents no changes, "" represents deleting the image, "<url>" represents a change
  const [fileUrl, setFileUrl] = useState(null)
  const [fileBlob, setFileBlob] = useState(null)
  const [open, setOpen] = useState(false)
  const [isCompressing, setIsCompressing] = useState(false)
  const imageFileInputRef = useRef(null)

  /**
   * Validate image parameters and generate thumbnail.
   *
   * @param {SyntheticEvent} e
   */
  const handleImageChange = async e => {
    e.persist()
    const file = e.target.files[0]

    setError(null)

    if (!file) {
      return
    }

    const allowedTypes = ["image/jpg", "image/jpeg", "image/png"]

    if (!allowedTypes.includes(file.type)) {
      setError("File must be one of jpg, jpeg, png")
      return
    }

    try {
      setIsCompressing(true)
      const compressedImage = await imageCompression(file, {
        maxSizeMB: 2,
      })
      
      // Triggered by readAsDataURL
      fileReader.onloadend = () => {
        setFileUrl(fileReader.result)
        setIsCompressing(false)

        // create image element in order to capture pixel dimensions
        const img = new Image()
        img.src = fileReader.result
      }

      fileReader.readAsDataURL(compressedImage)
      setFileBlob(compressedImage)
    } catch {
      setError("Something went wrong uploading your image")
    }
  }

  // Only display removal button if there's something to remove
  const enableRemoval = !isCompressing && (fileBlob || (profile?.wallpaperImageUrl && fileUrl === null))

  const handleRemoveWallpaperImage = () => {
    if (isCompressing) {
      setError("Please wait for your file to finish loading.")
      return
    } else if (!enableRemoval) {
      setError("There is no wallpaper image to remove")
      return
    }

    setFileUrl(profile.wallpaperImageUrl ? "" : null)
    setFileBlob(null)
    setError(null)
    imageFileInputRef.current.value = "" // clear any locally uploaded images 
  }

  const handleClose = () => {
    setOpen(false)
    setFileUrl(null)
    setFileBlob(null)
  }

  const handleSubmit = async () => {
    if (isCompressing) {
      setError("Please wait for your file to finish loading.")
      return
    } else if (fileUrl === null) {
      setError("You haven't made any modifications to save")
      return
    }

    try {
      if (fileUrl === "") {
        await firebase.deleteWallpaperImage()
        await firebase.updateProfile({ data: { wallpaperImageUrl: deleteField() } })
      } else {
        const fileURL = await firebase.storeWallpaperImage(fileBlob)
        if (fileURL) {
          await firebase.updateProfile({ data: { wallpaperImageUrl: fileURL } })
        }
      }
    } catch (e) {
      setError(
        "There was a problem updating your wallpaper. Please try again later."
      )
    }

    handleClose()
  }

  return (
    <>
      <button
        style={{ backgroundColor: "transparent", display: "flex", justifyContent: "center", alignItems: "center" }}
        onClick={() => {
          setError(null)
          setOpen(true)
        }}
      >
        <FontAwesomeIcon icon={faImages} />
      </button>
      <WingsModal
        isOpen={open}
        size="lg"
        onClose={handleClose}
        className="portfolio-wallpaper-image-modal"
        headerAlign="center"
        title={<FormattedMessage id="portfolio-wallpaper-image-change-wallpaper" defaultMessage="Change Wallpaper" />}
        titleLevel={5}
        secondaryActionLabel={<FormattedMessage id="dashboard-cancel" defaultMessage="Cancel"/>}
        onSecondaryAction={handleClose}
        primaryActionLabel={<FormattedMessage id="dashboard-this-week-save-button" defaultMessage="Save"/>}
        onPrimaryAction={handleSubmit}
      >
        <WingsModal.Body className="portfolio-wallpaper-image-modal-body">
          <div>
            {/* <h6><FormattedMessage
              id="portfolio-wallpaper-image-wallpaper-preview"
              defaultMessage="Wallpaper Preview"
            /></h6> */}
            <div className="wallpaper-image-and-remove-button-container">
              <div className="wallpaper-image-preview-container">
                <WallpaperImage
                  profile={profile} 
                  potentialReplacementURL={fileUrl}
                  isLoading={isCompressing}
                />
                {enableRemoval && (
                  <button className="remove-wallpaper-image-button" onClick={handleRemoveWallpaperImage}>
                    <FontAwesomeIcon icon={faTrashRegular} className="show-by-default" />
                    <FontAwesomeIcon icon={faTrashSolid} className="show-on-hover" />
                  </button>
                )}
              </div>
            </div>
              <div 
                className="form-element portfolio-image-modal-upload-section" 
                style={{borderColor: error ? "red" : ""}}>
                <label className="custom-file-input-label" htmlFor="profileImage">
                  <FormattedMessage id="portfolio-image-modal-choose-image" defaultMessage="Choose image" />
                </label>
                <input
                  ref={imageFileInputRef}
                  id="profileImage"
                  name="profileImage"
                  type="file"
                  onChange={handleImageChange}
                />
                {error ? (
                  <small className="profile-image-modal-error-message">{error}</small>
                ) : imageFileInputRef.current?.files.length ? (
                  imageFileInputRef.current.files[0].name
                ) : (
                  <span id="profileImageLabel">
                    <FormattedMessage id="portfolio-image-modal-no-image-chosen" defaultMessage="No image chosen" />
                  </span>
                )}
              </div>
          </div>
        </WingsModal.Body>
      </WingsModal>
    </>
  )
}

export default WallpaperImageModal
